import { useState, type ReactNode } from 'react'
import { ActualHistory } from '@/components/custom/ogsm/actual-history'
import { ScorecardDatePicker } from '@/components/custom/scorecard/scorecard-date-picker'
import { ScorecardTargetValueInput } from '@/components/custom/scorecard/scorecard-target-value-input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Typography } from '@/components/ui/typography'
import { todayIsoDate } from '@/features/ogsm/actual-entry'
import { useBoardEdit } from '@/features/ogsm/board-edit-context'
import { ogsmErrorMessage } from '@/features/ogsm/errors'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { toScorecardDataType } from '@/features/ogsm/target-format'
import { useOgsmApi } from '@/hooks/use-ogsm'
import { useAddOgsmActualMutation } from '@/hooks/use-ogsm-data'
import { formatDate } from '@/lib/format'
import type { OgsmBoardNode } from '@/types/api'

function ActualForm({
  node,
  onDone,
}: {
  node: OgsmBoardNode
  onDone: () => void
}) {
  const { companyId, session } = useBoardEdit()
  const latest = node.latestActual
  const [value, setValue] = useState(latest?.value ?? Number.NaN)
  const [asOfDate, setAsOfDate] = useState(todayIsoDate)
  const unchanged = latest?.value === value && latest.asOfDate === asOfDate
  const add = useAddOgsmActualMutation(useOgsmApi(companyId), session.id)
  const valueId = `actual-value-${node.id}`
  const dateId = `actual-date-${node.id}`

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        add.mutate(
          { nodeId: node.id, input: { value, asOfDate } },
          { onSuccess: onDone }
        )
      }}
    >
      <div className="flex flex-col gap-1">
        <Label htmlFor={valueId}>{OGSM_COPY.actualValue}</Label>
        <ScorecardTargetValueInput
          id={valueId}
          value={value}
          onChange={setValue}
          dataType={toScorecardDataType(node.targetType)}
          unitLabel={node.targetType === 'NUMBER' ? node.descriptor : null}
        />
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={dateId}>{OGSM_COPY.actualDate}</Label>
        <ScorecardDatePicker
          id={dateId}
          value={asOfDate}
          onChange={setAsOfDate}
        />
        {latest && asOfDate < latest.asOfDate && (
          <Typography variant="body-sm" className="text-primary/60">
            {OGSM_COPY.newerActualShown(formatDate(latest.asOfDate))}
          </Typography>
        )}
      </div>
      {add.isError && (
        <Typography variant="body-sm" className="text-destructive" role="alert">
          {ogsmErrorMessage(add.error)}
        </Typography>
      )}
      <Button
        type="submit"
        size="sm"
        className="self-end"
        disabled={!Number.isFinite(value) || unchanged || add.isPending}
      >
        {OGSM_COPY.saveActual}
      </Button>

      <ActualHistory
        companyId={companyId}
        node={node}
        layout="stack"
        className="border-t border-primary/5 pt-3"
      />
    </form>
  )
}

/**
 * Where an actual is entered, on the board's actual line. The caller renders it
 * only when the backend says the node takes actuals (`actualEditable`). With no
 * line to wrap yet, it offers "Enter actual" in its place.
 */
export function ActualPopover({
  node,
  title,
  children,
}: {
  node: OgsmBoardNode
  title?: string
  children?: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const label = children ? OGSM_COPY.updateActual : OGSM_COPY.enterActual

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          title={title}
          // Opens only: the default toggle would close it again on a double-click.
          onClick={(event) => {
            event.preventDefault()
            setOpen(true)
          }}
          className="cursor-pointer text-left focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
        >
          {children ?? (
            <Typography
              as="span"
              variant="body-sm"
              className="text-secondary hover:underline"
            >
              {label}
            </Typography>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72">
        <ActualForm node={node} onDone={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  )
}
